import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, EventEmitter, QueryList, SimpleChanges, ViewChildren, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { ProductV2Variation } from '../../../core/models/product-v2.model';
import { isBlackFridayPlan } from '../../../core/utils/black-friday.util';
import { I18nPipe } from '../../pipes/i18n.pipe';
import {
  isRegistryDomainProductUuid,
  isRegistryDomainVariationUuid,
} from '../../../core/utils/cart-domain-suggestion-rules';

@Component({
  selector: 'umkt-expansable-card',
  standalone: true,
  imports: [CommonModule, I18nPipe],
  templateUrl: './expansable-card.component.html',
  styleUrls: ['./expansable-card.component.scss'],
})
export class ExpansableCardComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @HostBinding('class.expansable-host--mobile-layout')
  get hostMobileLayout(): boolean {
    return this.isMobileView;
  }

  @Input() plans: ProductV2Variation[] = [];
  @Input() selectedPlanId: number = 0;
  @Input() currentPlanId: number | null = null;
  @Input() isChangePlan: boolean = false;
  @Input() syntheticLayoutWidth: number | null = null;
  @Input() productUuid: string = '';
  @Input() actionLabel = 'components.expansableCard.addToCart';

  @Output() selectVariation = new EventEmitter<number>();

  @Output() addToCart = new EventEmitter<{
    plan?: ProductV2Variation | null;
    quantity: number;
  }>();
  
  readonly isBlackFridayPlan = isBlackFridayPlan;

  sanitizedDescription: SafeHtml = '';

  quantity: number = 1;
  expandedMobileCardId: number | null = null;
  isMobileView: boolean = false;
  flagBelowPrice: boolean[] = [];
  private sanitizer = inject(DomSanitizer);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private flagWidth = 0;
  private resizeObserver?: ResizeObserver;
  private headersChanged?: { unsubscribe(): void };

  @ViewChildren('planHeader') private planHeaders?: QueryList<ElementRef<HTMLElement>>;


  ngOnInit() {
    if (typeof window !== 'undefined') {
      this.updateMobileView();
    }
    this.initializeSelectedPlan();
  }

  ngAfterViewInit(): void {
    this.headersChanged = this.planHeaders?.changes.subscribe(() => this.bindFlagLayout());
    this.bindFlagLayout();
  }

  ngOnDestroy(): void {
    this.headersChanged?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  private initializeSelectedPlan() {
    if (
      this.plans &&
      this.plans.length > 0 &&
      this.plans[this.selectedPlanId]
    ) {
      this.sanitizedDescription = this.sanitizer.bypassSecurityTrustHtml(
        this.plans[this.selectedPlanId].description
      );
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['plans'] && this.plans && this.plans.length > 0) {
      this.adjustPlanPrices();
      this.initializeSelectedPlan();
    }
    if (
      changes['selectedPlanId'] &&
      this.plans &&
      this.plans[this.selectedPlanId]
    ) {
      this.initializeSelectedPlan();
    }
    if (changes['syntheticLayoutWidth']) {
      this.updateMobileView();
    }
  }

  private adjustPlanPrices() {
    this.plans = this.plans.map(plan => {
      if (this.isRegistryDomainPlan(plan)) {
        return { ...plan };
      }

      let adjustedPrice = plan.price;
      if (plan.paymentRecurrence === 'annual') {
        adjustedPrice = plan.price / 12;
      }
      return {
        ...plan,
        price: adjustedPrice
      };
    });
  }

  @HostListener('window:resize')
  onResize() {
    if (typeof window !== 'undefined') {
      this.updateMobileView();
      this.placeFlags();
    }
  }

  private bindFlagLayout(): void {
    this.resizeObserver?.disconnect();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.placeFlags());
      this.planHeaders?.forEach(header => this.resizeObserver?.observe(header.nativeElement));
    }
    this.placeFlags();
  }

  private placeFlags(): void {
    const headers = this.planHeaders;
    if (!headers) {
      return;
    }

    const measured = headers.map(header => this.needsFlagBelowPrice(header.nativeElement));
    const anyBelow = measured.some(Boolean);
    const next = measured.map((below, index) => {
      if (!anyBelow) {
        return false;
      }
      const header = headers.get(index)?.nativeElement;
      return below || !!header?.querySelector('.plan-name-wrapper > .bf-flag');
    });
    const unchanged =
      next.length === this.flagBelowPrice.length &&
      next.every((below, index) => below === this.flagBelowPrice[index]);
    if (unchanged) {
      return;
    }

    this.flagBelowPrice = next;
    this.changeDetector.detectChanges();
  }

  private needsFlagBelowPrice(header: HTMLElement): boolean {
    const name = header.querySelector('.plan-name') as HTMLElement | null;
    const flag = header.querySelector('.plan-name-wrapper > .bf-flag') as HTMLElement | null;
    const price = header.querySelector('.price') as HTMLElement | null;
    if (!name || !flag || !price || header.clientWidth === 0) {
      return false;
    }

    if (flag.offsetWidth > 0) {
      this.flagWidth = flag.offsetWidth;
    }
    if (this.flagWidth === 0) {
      return false;
    }

    const headerGap = this.readGap(header);
    const wrapper = header.querySelector('.plan-name-wrapper') as HTMLElement | null;
    const wrapperGap = wrapper ? this.readGap(wrapper) : 0;
    const label = header.querySelector('.price-label') as HTMLElement | null;
    const fidelity = header.querySelector('.fidelity') as HTMLElement | null;
    const priceWidth = Math.max(
      price.offsetWidth,
      label?.offsetWidth ?? 0,
      fidelity?.offsetWidth ?? 0
    );
    const needed = name.scrollWidth + this.flagWidth + priceWidth + headerGap + wrapperGap;

    return needed > header.clientWidth + 1;
  }

  private readGap(element: HTMLElement): number {
    const style = getComputedStyle(element);
    return parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
  }

  updateMobileView() {
    const widthFromPreview =
      this.syntheticLayoutWidth != null && this.syntheticLayoutWidth > 0
        ? this.syntheticLayoutWidth
        : null;
    const effectiveWidth =
      widthFromPreview ??
      (typeof window !== 'undefined' ? window.innerWidth : 1200);

    this.isMobileView = effectiveWidth < 1040;
    if (!this.isMobileView) {
      this.expandedMobileCardId = null;
    }
  }

  selectPlan(index: number) {
    if (!this.plans || !this.plans[index]) return;
    if (this.currentPlanId === index) return;

    this.selectedPlanId = index;
    this.selectVariation.emit(index);
    this.quantity = 1;
    this.sanitizedDescription = this.sanitizer.bypassSecurityTrustHtml(
      this.plans[index].description
    );

  }

  incrementQuantity(event: Event) {
    event.stopPropagation();
    this.quantity++;
  }

  decrementQuantity(event: Event) {
    event.stopPropagation();
    if (this.quantity > 1) {
      this.quantity--;
    }
  }

  onAddToCart() {
    if (!this.plans || !this.plans[this.selectedPlanId]) {
      return
    }else if (this.plans.length === 0) {
      this.addToCart.emit({
        plan: null,
        quantity: this.quantity,
      });
    } else {
      this.addToCart.emit({
        plan: this.plans[this.selectedPlanId],
        quantity: this.quantity,
      });
    }
  }

  formatPrice(price: number): string {
    return price.toFixed(2).replace('.', ',');
  }

  toggleMobileDetails(index: number) {
    if (!this.isMobileView) return;
    if (this.expandedMobileCardId === index) {
      this.expandedMobileCardId = null;
    } else {
      this.expandedMobileCardId = index;
    }
  }

  isMobileDetailsOpen(index: number): boolean {
    return this.isMobileView && this.expandedMobileCardId === index;
  }

  isRegistryDomainPlan(plan: ProductV2Variation): boolean {
    if (isRegistryDomainVariationUuid(plan.uuid)) {
      return true;
    }
    return isRegistryDomainProductUuid(this.productUuid);
  }

  getPeriodLabelKey(plan: ProductV2Variation): string {
    return this.isRegistryDomainPlan(plan)
      ? 'components.expansableCard.perYear'
      : 'components.expansableCard.perMonth';
  }
}
